/**
 * Bringing someone else's artwork onto a template.
 *
 * Photoshop, Illustrator or SVG, read in the browser, shown as the layer list
 * the shop will recognise, and imported only once they have seen what is about
 * to happen. What each format can honestly give differs, so the notes say so
 * here rather than leaving the shop to discover it on the page.
 *
 * Only ever opened while the shop is drawing a template: the readers are large
 * libraries, fetched on demand, and a customer never meets them.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { placeImport, pathsOf, type Fitting } from '../import/place';
import { readPsd } from '../import/psd';
import { readIllustrator, readSvg, readerFor } from '../import/vector';
import { ImportProblem, type ImportDoc, type ImportPiece } from '../import/types';
import { fontFamilies } from '../text/fonts';
import { useEditor } from '../state/store';

/** The shop's font, by the name the file used, or nothing. */
function matchFont( asked: string ): string | null {
  if ( ! asked ) return null;

  const plain = ( name: string ) => name.toLowerCase().replace( /[^a-z]/g, '' );
  const wanted = plain( asked );

  // "TrebuchetMS-Bold" is Trebuchet MS; the weight is read separately.
  const stem = wanted.replace( /(bold|italic|oblique|regular|light|medium|semibold|black)+$/, '' );

  for ( const family of fontFamilies() ) {
    const have = plain( family );

    if ( have === wanted || have === stem ) return family;
  }

  return null;
}

export function ImportDialog( { onClose }: { onClose: () => void } ) {
  const page = useEditor( ( s ) => s.page() );
  const input = useRef<HTMLInputElement>( null );
  const [ reading, setReading ] = useState( false );
  const [ doc, setDoc ] = useState<ImportDoc | null>( null );
  const [ chosen, setChosen ] = useState<Set<string>>( new Set() );
  const [ fitting, setFitting ] = useState<Fitting>( 'scale' );
  const [ problem, setProblem ] = useState( '' );
  const [ placing, setPlacing ] = useState( false );

  useEffect( () => {
    const key = ( event: KeyboardEvent ) => {
      if ( event.key === 'Escape' ) onClose();
    };

    window.addEventListener( 'keydown', key );

    return () => window.removeEventListener( 'keydown', key );
  }, [ onClose ] );

  const read = useCallback( async ( file: File ) => {
    const which = readerFor( file );

    if ( ! which ) {
      setProblem( 'Choose a Photoshop (.psd), Illustrator (.ai) or SVG file.' );

      return;
    }

    setProblem( '' );
    setReading( true );
    setDoc( null );

    try {
      const read =
        which === 'psd'
          ? await readPsd( file, matchFont )
          : which === 'svg'
            ? await readSvg( file, matchFont )
            : await readIllustrator( file );

      setDoc( read );
      setChosen( new Set( pathsOf( read.pieces ) ) );
    } catch ( error ) {
      setProblem( error instanceof ImportProblem ? error.message : 'That file could not be read.' );
    } finally {
      setReading( false );
    }
  }, [] );

  const bring = async () => {
    if ( ! doc ) return;

    setPlacing( true );

    const trouble = await placeImport( doc, chosen, fitting );

    setPlacing( false );

    if ( trouble.length ) {
      setProblem( trouble.join( ' ' ) );

      return;
    }

    onClose();
  };

  const sizeDiffers =
    !! doc && !! page && ( Math.abs( doc.width - page.size.w ) > 0.5 || Math.abs( doc.height - page.size.h ) > 0.5 );

  return (
    <div className="tb-import" role="dialog" aria-modal="true" aria-label="Import artwork">
      <div className="tb-import__sheet">
        <div className="tb-import__head">
          <h2 className="tb-import__title">Import artwork</h2>
          <p className="tb-import__sub">
            A Photoshop, Illustrator or SVG file becomes the layers of this template. Everything imported is fixed for
            customers; untick any layer in the Layers panel to hand it back.
          </p>
          <button type="button" className="tb-import__close" aria-label="Close" onClick={ onClose }>
            ×
          </button>
        </div>

        <div className="tb-import__body">
          <input
            ref={ input }
            type="file"
            accept=".psd,.psb,.svg,.ai,.pdf"
            hidden
            onChange={ ( event ) => {
              const file = event.target.files?.[ 0 ];

              event.target.value = '';

              if ( file ) void read( file );
            } }
          />

          <button type="button" className="tb-btn tb-btn--primary" disabled={ reading } onClick={ () => input.current?.click() }>
            { reading ? 'Reading…' : doc ? 'Choose another file' : 'Choose a file' }
          </button>

          { problem && <p className="tb-import__problem">{ problem }</p> }

          { doc && (
            <>
              <p className="tb-import__size">
                { doc.source === 'psd' ? 'Photoshop' : doc.source === 'ai' ? 'Illustrator' : 'SVG' } file,{ ' ' }
                { doc.width } × { doc.height } mm
                { sizeDiffers && page ? ` — this page is ${ page.size.w } × ${ page.size.h } mm` : '' }
              </p>

              { sizeDiffers && (
                <div className="tb-import__fit" role="group" aria-label="Size">
                  <label>
                    <input type="radio" name="tb-import-fit" checked={ fitting === 'scale' } onChange={ () => setFitting( 'scale' ) } />
                    <span>Scale it to fit this page</span>
                  </label>
                  <label>
                    <input type="radio" name="tb-import-fit" checked={ fitting === 'place' } onChange={ () => setFitting( 'place' ) } />
                    <span>Keep its own size</span>
                  </label>
                </div>
              ) }

              { doc.notes.map( ( note ) => (
                <p key={ note } className="tb-import__note">{ note }</p>
              ) ) }

              <ul className="tb-import__layers">
                { rows( doc.pieces ).map( ( row ) => (
                  <li key={ row.path } style={ { paddingInlineStart: `${ row.depth * 16 }px` } }>
                    { row.kind === 'group' ? (
                      <span className="tb-import__group">{ row.name }</span>
                    ) : (
                      <label>
                        <input
                          type="checkbox"
                          checked={ chosen.has( row.path ) }
                          onChange={ ( event ) =>
                            setChosen( ( was ) => {
                              const next = new Set( was );

                              if ( event.target.checked ) next.add( row.path );
                              else next.delete( row.path );

                              return next;
                            } )
                          }
                        />
                        <span className="tb-import__name">{ row.name }</span>
                        <span className="tb-import__kind">
                          { row.kind === 'text' ? 'text' : row.kind === 'outlines' ? 'outlines' : 'picture' }
                          { row.hidden ? ' · hidden' : '' }
                        </span>
                      </label>
                    ) }
                  </li>
                ) ) }
              </ul>
            </>
          ) }
        </div>

        <div className="tb-import__foot">
          <button type="button" className="tb-btn" onClick={ onClose }>
            Cancel
          </button>
          <button type="button" className="tb-btn tb-btn--primary" disabled={ ! doc || ! chosen.size || placing } onClick={ () => void bring() }>
            { placing ? 'Importing…' : `Import ${ chosen.size } layer${ chosen.size === 1 ? '' : 's' }` }
          </button>
        </div>
      </div>
    </div>
  );
}

/** The pieces flattened into rows, so a group and its children read as a list. */
function rows( pieces: ImportPiece[], depth = 0, path = '' ): Array<{ path: string; name: string; kind: ImportPiece['kind']; hidden: boolean; depth: number }> {
  return pieces.flatMap( ( piece ) => {
    const here = path ? `${ path } / ${ piece.name }` : piece.name;
    const row = { path: here, name: piece.name, kind: piece.kind, hidden: piece.hidden, depth };

    return piece.kind === 'group' ? [ row, ...rows( piece.children ?? [], depth + 1, here ) ] : [ row ];
  } );
}
